import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Zap, Smile, Award, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
export function HowItWorksSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = ['https://cdn.magicpatterns.com/uploads/mcZcWbLdbbXWzhRjQQ4VhF/slide_1.png', 'https://cdn.magicpatterns.com/uploads/uPazT7hfiUM4HnJUTVhm8x/slide_2.png', 'https://cdn.magicpatterns.com/uploads/9RLMqkwjT8wULVprzoV2iG/slide_3.png', 'https://cdn.magicpatterns.com/uploads/jzZUQ6kVvP8qCkwXxmk4ba/slide_4.png', 'https://cdn.magicpatterns.com/uploads/p5jGeSydFBdhZATW9YEspB/slide_5.png'];
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };
  const steps = [{
    icon: BookOpen,
    number: '01',
    title: 'Abre el Ebook',
    description: 'Elige una de las 30 actividades. Toma 10 segundos. Sin preparación, sin materiales complicados.',
    detail: 'Cada actividad está diseñada para ser inmediata',
    color: 'from-brand-red/10 to-brand-orange/10',
    iconColor: 'text-brand-red'
  }, {
    icon: Zap,
    number: '02',
    title: 'Tu Hijo Se Enfoca',
    description: 'La actividad captura su atención de inmediato. Diseñadas psicológicamente para enganchar sin sobreestimular.',
    detail: 'Basadas en principios Montessori probados',
    color: 'from-brand-yellow/10 to-brand-green/10',
    iconColor: 'text-brand-yellow'
  }, {
    icon: Smile,
    number: '03',
    title: 'Tú Respiras',
    description: '20 minutos de calma real. Cocinas, trabajas, o simplemente te sientas. Sin lucha, sin culpa, sin pantallas.',
    detail: 'Paz predecible cuando más la necesitas',
    color: 'from-brand-green/10 to-brand-navy/10',
    iconColor: 'text-brand-green'
  }];
  const principles = [{
    icon: '⚡',
    title: 'Simple',
    description: 'Menos de 10 segundos para empezar. Sin preparación.'
  }, {
    icon: '🎯',
    title: 'Efectivo',
    description: 'Funciona cuando estás cansada. Baja fricción.'
  }, {
    icon: '🔄',
    title: 'Autónomo',
    description: 'Crea hábitos. No depende de tu motivación.'
  }, {
    icon: '💝',
    title: 'Gratificante',
    description: 'Recompensa intrínseca inmediata para tu hijo.'
  }];
  return <section className="relative py-32 overflow-hidden bg-gradient-to-b from-white via-brand-cream/30 to-brand-offwhite" data-id="element-356">
      {/* Elegant background */}
      <div className="absolute inset-0 opacity-[0.02]" data-id="element-357">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, #558B2F 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-358" />
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-brand-green/10 to-brand-yellow/10 rounded-full blur-3xl" data-id="element-359" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-brand-navy/10 to-brand-red/10 rounded-full blur-3xl" data-id="element-360" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-361">
        {/* COMPACT LUXURY SLIDESHOW */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-362">
          <div className="max-w-3xl mx-auto" data-id="element-363">
            {/* Luxury frame container */}
            <div className="relative" data-id="element-364">
              {/* Golden frame effect */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#FFD700]/20 via-[#FFA500]/10 to-[#FFD700]/20 rounded-3xl blur-xl" data-id="element-365" />

              {/* Main slideshow container */}
              <div className="relative bg-gradient-to-br from-[#1a1a1a] to-black rounded-2xl overflow-hidden shadow-2xl border-2 border-[#FFD700]/30" data-id="element-366">
                {/* Slideshow */}
                <div className="relative aspect-[4/3] bg-white" data-id="element-367">
                  <AnimatePresence mode="wait" data-id="element-368">
                    <motion.img key={currentSlide} src={slides[currentSlide]} alt={`Slide ${currentSlide + 1}`} initial={{
                    opacity: 0,
                    scale: 1.05
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.95
                  }} transition={{
                    duration: 0.6,
                    ease: 'easeInOut'
                  }} className="w-full h-full object-contain" data-id="element-369" />
                  </AnimatePresence>

                  {/* Navigation arrows - Luxury style */}
                  <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 group z-10" aria-label="Anterior" data-id="element-370">
                    <ChevronLeft className="w-5 h-5 text-black group-hover:-translate-x-0.5 transition-transform" data-id="element-371" />
                  </button>

                  <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 group z-10" aria-label="Siguiente" data-id="element-372">
                    <ChevronRight className="w-5 h-5 text-black group-hover:translate-x-0.5 transition-transform" data-id="element-373" />
                  </button>

                  {/* Slide counter - Luxury badge */}
                  <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm px-4 py-2 rounded-full border border-[#FFD700]/30 z-10" data-id="element-374">
                    <span className="text-[#FFD700] text-sm font-bold" data-id="element-375">
                      {currentSlide + 1} / {slides.length}
                    </span>
                  </div>
                </div>

                {/* Bottom indicators - Luxury style */}
                <div className="bg-gradient-to-r from-black via-[#1a1a1a] to-black px-6 py-4 flex items-center justify-center gap-2" data-id="element-376">
                  {slides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`relative h-1.5 rounded-full transition-all duration-500 ${index === currentSlide ? 'w-12 bg-gradient-to-r from-[#FFD700] to-[#FFA500]' : 'w-1.5 bg-gray-600 hover:bg-gray-500'}`} aria-label={`Ir a slide ${index + 1}`} data-id="element-377">
                      {index === currentSlide && <motion.div layoutId="activeSlide" className="absolute inset-0 bg-gradient-to-r from-[#FFD700] to-[#FFA500] rounded-full" style={{
                    boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
                  }} data-id="element-378" />}
                    </button>)}
                </div>

                {/* Decorative corner accents */}
                <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[#FFD700]/50 rounded-tl-2xl" data-id="element-379" />
                <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-[#FFD700]/50 rounded-tr-2xl" data-id="element-380" />
                <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-[#FFD700]/50 rounded-bl-2xl" data-id="element-381" />
                <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[#FFD700]/50 rounded-br-2xl" data-id="element-382" />
              </div>

              {/* Floating particles effect */}
              <div className="absolute -top-2 left-1/4 w-2 h-2 bg-[#FFD700] rounded-full animate-ping opacity-75" data-id="element-383" />
              <div className="absolute -bottom-2 right-1/4 w-2 h-2 bg-[#FFA500] rounded-full animate-ping opacity-75" style={{
              animationDelay: '1s'
            }} data-id="element-384" />
            </div>
          </div>
        </motion.div>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-20" data-id="element-385">
          <div className="inline-block px-5 py-2 bg-brand-green/10 text-brand-green rounded-full font-bold text-sm mb-6 border border-brand-green/20" data-id="element-386">
            Sistema Elegante y Efectivo
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-brand-navy mb-6 font-serif" data-id="element-387">
            Cómo Funciona el
            <span className="block text-brand-green italic mt-2" data-id="element-388">
              Plan Anti-Pantallas
            </span>
          </h2>

          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed" data-id="element-389">
            Tres pasos simples que transforman el caos de las 6:40 pm
            <br data-id="element-390" />
            en{' '}
            <span className="font-semibold text-brand-navy" data-id="element-391">
              20 minutos de paz predecible.
            </span>
          </p>
        </motion.div>

        {/* Steps */}
        <div className="max-w-5xl mx-auto mb-24" data-id="element-392">
          <div className="relative" data-id="element-393">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-red/20 via-brand-yellow/40 to-brand-green/20" data-id="element-394" />

            <div className="grid md:grid-cols-3 gap-8 relative z-10" data-id="element-395">
              {steps.map((step, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: i * 0.2
            }} className="relative" data-id="element-396">
                  {/* Step card */}
                  <div className={`bg-gradient-to-br ${step.color} rounded-2xl p-8 border-2 border-white shadow-xl h-full`} data-id="element-397">
                    {/* Number badge */}
                    <div className="absolute -top-4 -right-4 w-12 h-12 bg-brand-navy text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg border-4 border-white" data-id="element-398">
                      {step.number}
                    </div>

                    {/* Icon */}
                    <div className={`w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-md`} data-id="element-399">
                      <step.icon className={`w-8 h-8 ${step.iconColor}`} data-id="element-400" />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-brand-navy mb-3 font-serif" data-id="element-401">
                      {step.title}
                    </h3>
                    <p className="text-slate-700 leading-relaxed mb-4" data-id="element-402">
                      {step.description}
                    </p>
                    <div className="pt-4 border-t border-brand-navy/10" data-id="element-403">
                      <p className="text-sm text-brand-navy/70 italic" data-id="element-404">
                        {step.detail}
                      </p>
                    </div>
                  </div>

                  {/* Arrow between steps */}
                  {i < steps.length - 1 && <div className="hidden md:block absolute top-24 -right-4 z-20" data-id="element-405">
                      <ArrowRight className="w-8 h-8 text-brand-navy/20" data-id="element-406" />
                    </div>}
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* Principles section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-5xl mx-auto" data-id="element-407">
          <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-10 md:p-12 border-2 border-brand-navy/10 shadow-2xl" data-id="element-408">
            <div className="text-center mb-10" data-id="element-409">
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-brand-navy text-white rounded-full font-bold text-sm mb-4" data-id="element-410">
                <Award className="w-4 h-4" data-id="element-411" />
                <span data-id="element-412">CRITERIOS DE UN SISTEMA EFECTIVO</span>
              </div>
              <h3 className="text-3xl font-bold text-brand-navy font-serif mb-4" data-id="element-413">
                Por Qué Este Sistema Funciona
              </h3>
              <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-414">
                Para reducir el tiempo de pantalla de forma sostenible, no basta
                con prohibir. Necesitas reemplazar el hábito con algo igual de
                gratificante pero saludable.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-415">
              {principles.map((principle, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-brand-cream/50 rounded-xl p-6 border border-brand-navy/10" data-id="element-416">
                  <div className="flex items-start gap-4" data-id="element-417">
                    <div className="text-4xl" data-id="element-418">{principle.icon}</div>
                    <div className="flex-1" data-id="element-419">
                      <h4 className="font-bold text-brand-navy text-lg mb-2" data-id="element-420">
                        {principle.title}
                      </h4>
                      <p className="text-slate-700 text-sm leading-relaxed" data-id="element-421">
                        {principle.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* Bottom highlight */}
            <div className="mt-10 pt-8 border-t border-brand-navy/10 text-center" data-id="element-422">
              <p className="text-lg text-brand-navy italic font-serif" data-id="element-423">
                "Un sistema que funciona incluso cuando estás cansada.
                <br data-id="element-424" />
                Esa es la diferencia entre teoría y transformación real."
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}